import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from "react";
import { formatDate, hasDatePassed } from "core/helpers/dates";
import { useModalContext } from "shared/core/state/ModalContext/ModalContext";
import { AppReviewState, ReviewStateKey, TriggerStatus } from "./appReviewState";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { isAppReviewRequestAvailable, onAppReviewTrigger } from "core/javascriptChannels";
import { addDays } from "date-fns";
import { sanitizeField } from "core/helpers/utils";
import { ExperienceModal } from "UIPalette/ExperienceModal/ExperienceModal";

export type AppReviewContextType = {
  isExperienceModalVisible: boolean;
  isFeedbackModalVisible: boolean;
  handlePositiveMoment: (delayExperienceModal?: boolean) => Promise<void>;
  handlePositiveExperience: () => Promise<void>;
  handleNegativeExperience: () => void;
  handleDismissExperience: () => void;
  handleSubmitFeedback: (feedback: string) => Promise<void>;
  handleDismissFeedback: () => void;
};

export const AppReviewContext = createContext({} as AppReviewContextType);

export const useAppReviewContext = () => useContext(AppReviewContext);

export function AppReviewContextProvider({ children }: PropsWithChildren) {
  const { show: showExperienceModal, isVisible: isExperienceModalVisible, hide: hideExperienceModal } = useModalContext("user-experience");
  const { show: showFeedbackModal, isVisible: isFeedbackModalVisible, hide: hideFeedbackModal } = useModalContext("critical-feedback");

  const getTriggerDate = () => AppReviewState.get(ReviewStateKey.TriggerDate);
  const getTriggerStatus = () => AppReviewState.get(ReviewStateKey.TriggerStatus);
  const getPositiveMoment = () => Number(AppReviewState.get(ReviewStateKey.PositiveMoment) ?? "");

  const getAddedDays = (days: number) => addDays(new Date(), days);

  const handlePositiveMoment = useCallback(
    async (delayExperienceModal?: boolean) => {
      try {
        const isAppReviewAvailable = await isAppReviewRequestAvailable();
        const hasPassedTriggerDate = hasDatePassed(new Date(getTriggerDate() ?? 0));

        if (!!isAppReviewAvailable && getTriggerStatus() !== TriggerStatus.Inactive) {
          const positiveMomentThreshold = AppReviewState.getThreshold();
          const hasHitThreshold = getPositiveMoment() + 1 >= positiveMomentThreshold;
          const isAcceptedTriggerDate = !getTriggerDate() || hasPassedTriggerDate;

          if (hasHitThreshold && isAcceptedTriggerDate && !delayExperienceModal) {
            trackEvents(EventNames.SHOW_EXPERIENCE_MODAL);
            showExperienceModal();
            return;
          }

          if (getTriggerStatus() !== TriggerStatus.Inactive) {
            AppReviewState.set(ReviewStateKey.PositiveMoment, getPositiveMoment() + 1);
            return;
          }
        }
      } catch (err) {
        console.error({ err });
      }
    },
    [showExperienceModal]
  );

  const handlePositiveExperience = useCallback(async () => {
    try {
      await onAppReviewTrigger();
      trackEvents(EventNames.EXPERIENCE_THUMBS_UP);
      AppReviewState.set(ReviewStateKey.TriggerStatus, TriggerStatus.Inactive);
      AppReviewState.clear(ReviewStateKey.TriggerDate);
      AppReviewState.clear(ReviewStateKey.PositiveMoment);
      hideExperienceModal();
    } catch (err) {
      console.error({ err });
    }
  }, [hideExperienceModal]);

  const handleNegativeExperience = useCallback(() => {
    trackEvents(EventNames.EXPERIENCE_THUMBS_DOWN);
    AppReviewState.set(ReviewStateKey.TriggerDate, formatDate(getAddedDays(20)));
    AppReviewState.set(ReviewStateKey.TriggerStatus, TriggerStatus.Active);
    AppReviewState.clear(ReviewStateKey.PositiveMoment);
    hideExperienceModal();
    showFeedbackModal();
  }, [hideExperienceModal, showFeedbackModal]);

  const handleDismissExperience = useCallback(() => {
    trackEvents(EventNames.CLOSE_EXPERIENCE_MODAL);
    AppReviewState.set(ReviewStateKey.TriggerDate, formatDate(getAddedDays(60)));
    AppReviewState.set(ReviewStateKey.TriggerStatus, TriggerStatus.Active);
    AppReviewState.clear(ReviewStateKey.PositiveMoment);
    hideExperienceModal();
  }, [hideExperienceModal]);

  const handleSubmitFeedback = useCallback(
    async (message: string) => {
      const feedback = sanitizeField(message);
      trackEvents(EventNames.APP_EXPERIENCE_FEEDBACK, { feedback });
      hideFeedbackModal();
    },
    [hideFeedbackModal]
  );

  const handleDismissFeedback = useCallback(() => {
    trackEvents(EventNames.DISMISS_EXPERIENCE_FEEDBACK);
    hideFeedbackModal();
  }, [hideFeedbackModal]);

  const contextValue = useMemo(
    () => ({
      isExperienceModalVisible,
      isFeedbackModalVisible,
      handlePositiveMoment,
      handlePositiveExperience,
      handleNegativeExperience,
      handleDismissExperience,
      handleSubmitFeedback,
      handleDismissFeedback,
    }),
    [
      handleDismissExperience,
      handleNegativeExperience,
      handlePositiveExperience,
      handlePositiveMoment,
      handleSubmitFeedback,
      isExperienceModalVisible,
      isFeedbackModalVisible,
      handleDismissFeedback,
    ]
  );

  return (
    <AppReviewContext.Provider value={contextValue}>
      {children}
      <ExperienceModal
        isVisible={isExperienceModalVisible}
        onPositive={handlePositiveExperience}
        onNegative={handleNegativeExperience}
        onDismiss={handleDismissExperience}
      />
    </AppReviewContext.Provider>
  );
}
